import { Injectable } from '@angular/core';
import { ValvePosition } from '../../model/controllerNotes/valvePosition';
import { UnitStatus } from '../../model/controllerNotes/unitStatus';
import {
  ControllerNote,
  ControllerNoteSortOrQuery,
  CreateControllerNoteInput,
  SCADAControlValue,
  UpdateControllerNoteInput,
} from 'src/app/model/controllerNotes/controllerNote';
import {
  getCorrectedDate,
  sortByProperty,
  SortDirection,
} from 'src/app/utils/utils';
import { Entry } from 'src/app/model/controllerNotes/entry';
import { FormGroup } from '@angular/forms';
import { LazyLoadFilters } from 'src/app/core/containers/search-container/search-container.component';
import { FilterMetadata } from '@openng/optimus-ui/api';
import {
  convertUserTimeBasedOnAOROffset,
  convertUTCTimeBasedOnAOROffset,
} from 'src/app/crm/functions/controller-note-functions';
import { MobileControllerNotesSearchRawValue } from 'src/app/model/controllerNotes/mobile-controller-notes-search-form';

@Injectable({
  providedIn: 'root',
})
export class ControllerNotesBuilderService {
  buildUnitStatuses(item: any): UnitStatus {
    const retVal = {
      id: item.id,
      name: item.name,
      enabled: item.enabled,
    };
    return retVal;
  }

  buildValvePositions(item: any): ValvePosition {
    const retVal = {
      id: item.id,
      name: item.name,
      enabled: item.enabled,
    };
    return retVal;
  }

  buildControllerNote(item: any, isAorView: boolean = false): ControllerNote {
    let note = {
      id: item.id,
      category: item.category,
      AORId: item.AORId,
      AORName: item.AORName,
      facilityId: item.facilityId,
      facilityName: item.facilityName,
      facilityUID: item.facilityUID,
      pipelineSection: item.pipelineSection,
      pipeline: item.pipeline,
      contactId: item.contactId,
      contactName: item.contactName,
      callerFirstName: item.callerFirstName,
      callerLastName: item.callerLastName,
      phoneNumber: item.phoneNumber,
      state: item.state,
      county: item.county,
      township: item.township,
      city: item.city,
      noteType: item.noteType,
      maop: item.maop,
      falseAlarm: item.falseAlarm,
      abnormalOperation: item.abnormalOperation,
      addToTurnover: item.addToTurnover,
      createdAt: isAorView
        ? convertUTCTimeBasedOnAOROffset(item.createdAt, item.AORName)!
        : getCorrectedDate(item.createdAt)!,
      createdBy: item.createdBy,
      updatedAt: isAorView
        ? convertUTCTimeBasedOnAOROffset(item.updatedAt, item.AORName)!
        : getCorrectedDate(item.updatedAt)!,
      updater: item.updater,
      system: item.systemName,
      entries: item.entries
        ? item.entries
            .map((entry: any) =>
              this.buildControllerNoteEntry(entry, isAorView, item.AORName)
            )
            .sort((a: Entry, b: Entry) =>
              sortByProperty(a, b, 'time', SortDirection.DESCENDING)
            )
        : [],
      displayDate: item.displayDate,
      firstEntry:
        item.entries?.length > 0
          ? this.getControllerNotesFirstEntryString(item.entries)
          : 'No notes found.',
      displayDateOnly: item.displayDateOnly,
      facilityOrPipelineNum: item.facilityName ?? item.pipeline,
      calloutInitiated: item.calloutInitiated,
      unit: item.unit,
      unitStatus: item.unitStatus,
      valvePosition: item.valvePosition,
      maintenance: item.maintenance,
      facilityScadaId: item.facilityScadaId,
      SCADAControl: item.SCADAControl,
    };

    if (!note.updatedAt) {
      note.updatedAt = note.createdAt;
    }

    return note;
  }

  buildControllerNoteEntry(
    item: any,
    isAorView: boolean,
    aorName: string
  ): Entry {
    return {
      id: item.id,
      controllerNotesId: item.controllerNotesId,
      time: isAorView
        ? convertUTCTimeBasedOnAOROffset(item.time, aorName)!
        : getCorrectedDate(item.time)!,
      user: item.user,
      note: item.note,
      createdBy: item.createdBy,
      updater: item.updater,
      createdAt: isAorView
        ? convertUTCTimeBasedOnAOROffset(item.createdAt, aorName)!
        : getCorrectedDate(item.createdAt)!,
      updatedAt: isAorView
        ? convertUTCTimeBasedOnAOROffset(item.updatedAt, aorName)!
        : getCorrectedDate(item.updatedAt)!,
    };
  }

  getControllerNotesFirstEntryString(entries: Entry[]): string {
    entries = entries.sort((a: Entry, b: Entry) =>
      sortByProperty(a, b, 'time', SortDirection.DESCENDING)
    );
    // 635 characters is roughly 3 lines worth of the note, should capture most of the note here while stripping html
    const firstEntryParsed = new DOMParser().parseFromString(
      entries[0]?.note.substring(0, 635),
      'text/html'
    ).body.firstChild?.textContent;

    if (!firstEntryParsed) {
      return '';
    }

    if (entries?.length === 1) {
      return firstEntryParsed;
    }

    return `(${entries?.length}) - ${firstEntryParsed}`;
  }

  buildControllerNoteInput(
    originalNote: ControllerNote,
    item: FormGroup,
    isAorView = false
  ): ControllerNote | CreateControllerNoteInput {
    let typeAORSection = item.controls['typeAOR'];
    let extraInfoSection = item.controls['contactLocInfo'];
    let initialNoteEntry = item.controls['initialNoteEntry'];
    console.log(extraInfoSection.get('SCADAControl'));

    const passedInCreated = typeAORSection.get('createdAt')?.value as Date;
    const correctedCreatedAt = isAorView
      ? convertUserTimeBasedOnAOROffset(
          passedInCreated,
          typeAORSection.get('aor')?.value.name ?? originalNote.AORName
        )
      : passedInCreated;

    // if one of these fields gets disabled for editing, we need
    // to make sure that we fall back to the original note
    // otherwise we may get undefined things and not be able to process
    const retVal: ControllerNote | CreateControllerNoteInput = {
      id: originalNote.id ?? '',
      createdAt: correctedCreatedAt ?? originalNote.createdAt,
      noteType: typeAORSection.get('noteType')?.value?.id,
      category:
        typeAORSection.get('category')?.value?.name ?? originalNote.category,
      AORId: typeAORSection.get('aor')?.value?.id ?? originalNote.AORId,
      AORName: typeAORSection.get('aor')?.value?.name ?? originalNote.AORName,
      maop: typeAORSection.get('maop')?.value ?? false,
      falseAlarm: typeAORSection.get('falseAlarm')?.value ?? false,
      abnormalOperation:
        typeAORSection.get('abnormalOperation')?.value ?? false,
      addToTurnover: typeAORSection.get('includeInTurnover')?.value ?? false,
      facilityId:
        extraInfoSection.get('facility')?.value?.id ?? originalNote.facilityId,
      facilityName:
        extraInfoSection.get('facility')?.value?.facilityName ??
        originalNote.facilityName ??
        '',
      facilityUID:
        extraInfoSection.get('facility')?.value?.facilityUid ??
        originalNote.facilityUID ??
        '',
      pipelineSection: extraInfoSection.get('pipelineSection')?.value,
      pipeline: extraInfoSection.get('pipelineNum')?.value,
      contactId: extraInfoSection.get('contact')?.value?.id ?? '',
      contactName: extraInfoSection.get('contact')?.value?.fullName ?? '',
      phoneNumber: extraInfoSection.get('contactPhone')?.value,
      callerFirstName:
        extraInfoSection.get('callerFirstName')?.value ??
        originalNote.callerFirstName ??
        '',
      callerLastName:
        extraInfoSection.get('callerLastName')?.value ??
        originalNote.callerLastName ??
        '',
      state:
        extraInfoSection.get('state')?.value?.name ?? originalNote.state ?? '',
      county:
        extraInfoSection.get('county')?.value?.name ??
        originalNote.county ??
        '',
      township:
        extraInfoSection.get('township')?.value?.name ??
        originalNote.township ??
        '',
      city: extraInfoSection.get('city')?.value ?? '',
      system: extraInfoSection.get('systemName')?.value ?? originalNote.system,
      calloutInitiated: typeAORSection.get('calloutInitiated')?.value ?? false,
      unit: extraInfoSection.get('unit')?.value ?? '',
      unitStatus:
        extraInfoSection.get('unitStatus')?.value ??
        originalNote.unitStatus ??
        null,
      maintenance: typeAORSection.get('maintenance')?.value ?? false,
      valvePosition:
        extraInfoSection.get('valvePosition')?.value ??
        originalNote.valvePosition ??
        null,
      SCADAControl: extraInfoSection.get('SCADAControl')?.value ?? null,
      initialNoteEntry: initialNoteEntry?.value,
    };
    return retVal;
  }

  buildNoteEntryInput(
    controllerNote: ControllerNote,
    isAorView: boolean,
    input: any
  ): Entry {
    return {
      id: input?.id,
      controllerNotesId: controllerNote?.id ?? '',
      time: isAorView
        ? (convertUserTimeBasedOnAOROffset(
            input.entryTime,
            controllerNote.AORName
          ) ?? input.entryTime)
        : input.entryTime,
      note: input.entryText,
      user: '',
    };
  }

  buildControllerNotesQuery(
    filters: LazyLoadFilters,
    startDate: Date,
    endDate: Date,
    hideMaintenance: boolean | null
  ): ControllerNoteSortOrQuery {
    const query: ControllerNoteSortOrQuery = {
      startDate: startDate,
      endDate: endDate,
      AORName: null,
      category: null,
      noteType: null,
      facilityOrPipelineNum: null,
      facilityScadaId: null,
      firstEntry: null,
      updatedAt: null,
      maintenance: null,
      businessUnits: null,
    };

    filters['maintenance'] = hideMaintenance as unknown as any;

    if (filters.startDate) {
      query.startDate = (filters.startDate as FilterMetadata).value;
    }
    if (filters.endDate) {
      query.endDate = (filters.endDate as FilterMetadata).value;
    }
    if (filters.AORName) {
      query.AORName = (filters.AORName as FilterMetadata).value;
    }
    if (filters.category) {
      query.category = (filters.category as FilterMetadata).value;
    }
    if (filters.noteType) {
      query.noteType = (filters.noteType as FilterMetadata).value;
    }
    if (filters.facilityOrPipelineNum) {
      query.facilityOrPipelineNum = (
        filters.facilityOrPipelineNum as FilterMetadata
      ).value;
    }
    if (filters.facilityScadaId) {
      query.facilityScadaId = (filters.facilityScadaId as FilterMetadata).value;
    }
    if (filters.firstEntry) {
      query.firstEntry = (filters.firstEntry as FilterMetadata).value;
    }
    if (filters.updatedAt) {
      query.updatedAt = (filters.updatedAt as FilterMetadata).value;
    }
    if (filters.maintenance) {
      query.maintenance = hideMaintenance;
    }
    if (filters.businessUnits) {
      query.businessUnits = filters.businessUnits as unknown as any;
    }

    return query;
  }

  buildControllerNotesMobileQuery(
    filters: MobileControllerNotesSearchRawValue
  ): ControllerNoteSortOrQuery {
    return {
      startDate: filters.startDate,
      endDate: filters.endDate,
      AORName: filters.aor,
      category: filters.category,
      noteType: filters.noteType,
      facilityOrPipelineNum: filters.facilityName,
      firstEntry: filters.noteText,
      maintenance: null,
    };
  }

  buildSCADAControlValue(item: any): SCADAControlValue {
    return {
      id: item.id,
      name: item.name,
      createdBy: item?.createdBy,
      createdAt: item?.createdAt,
      updatedAt: item?.updatedAt,
      updatedBy: item?.updatedBy,
    };
  }

  buildUpdateControllerNoteInput(
    note: ControllerNote | CreateControllerNoteInput
  ): UpdateControllerNoteInput {
    return {
      abnormalOperation: note.abnormalOperation,
      addToTurnover: note.addToTurnover,
      AOR: note.AORId!,
      city: note.city,
      contact: note.contactId,
      county: note.county,
      facilityId: note.facilityId,
      falseAlarm: note.falseAlarm,
      maop: note.maop,
      noteType: note.noteType,
      phoneNumber: note.phoneNumber,
      pipeline: note.pipeline,
      pipelineSection: note.pipelineSection,
      township: note.township,
      state: note.state,
      callerFirstName: note.callerFirstName,
      callerLastName: note.callerLastName,
      systemName: note.system,
      calloutInitiated: note.calloutInitiated,
      unit: note.unit,
      unitStatusId: note.unitStatus?.id,
      maintenance: note.maintenance,
      valvePositionId: note.valvePosition?.id,
      SCADAControlId: note.SCADAControl?.id
        ? note.SCADAControl?.id
        : (note.SCADAControl as unknown as string),
    };
  }
}
